import geolib from 'geolib';
import Vue from 'vue';

import { errorHandler } from '../../../tools';
import { mutationTypes, actionTypes, errorTypes } from '../../../types';
import { uiComponents, uiStates } from '../../../tools/viewState';

const loadedUrls = {};
const pendingUrls = new Set();
async function loadMapDataFromUrl(articleID, articleVersion, dataSourceURL) {
	const url = new URL(`./apps/article-${articleID}-${articleVersion}/index.json`, dataSourceURL).toString();

	let result;
	if (url in loadedUrls) {
		return loadedUrls[url];
	} else {
		if (pendingUrls.has(url)) return;
		pendingUrls.add(url);
		try {
			result = (await Vue.http.get(url)).body;
			pendingUrls.delete(url);
		} catch (error) {
			console.error(error);
			errorHandler(new Error(errorTypes.mapDownloadFailed));
			pendingUrls.delete(url);
			return;
		}
	}
	return result;
}

async function dispatchCustomMapArticle({
	customMap,
	dataSourceCFG,
	dispatch,
	isExhibitionMap,
	dataSourceURL,
}) {
	const { _id, version } = customMap;
	const article = dataSourceCFG.exhibitions.find(({ articleMap }) => _id in articleMap).articleMap[_id];
	const mapData = Object.assign(
		{},
		article.mapMetaData ? article.mapMetaData : await loadMapDataFromUrl(_id, version, dataSourceURL) // FIXME this is a legacy fallback
	);

	if (mapData.distortableImageCorners) {
		const { distortableImageCorners, languageContent } = article;
		dispatch(actionTypes.initOnlineGeoMap, { distortableImageCorners, languageContent, isExhibitionMap });
	} else {
		dispatch(actionTypes.loadCustomMap, {
			mapData,
			articleID: _id,
			articleVersion: version,
			isExhibitionMap,
		});
	}
}

export default {
	state: {
		mapData: {},
		tourPaths: [], // list of locationIDs
		tourLocations: {}, // `{[tourID]: [locationIDs], ...}`
		tourBounds: {},
		exhibitLocations: {},
		exhibitionLocationList: [], // list of locationIDs
		exhibitionPolygonLocations: {}, // the key is the exibition
		mapLayerToExhibitionId: [],
		mapAccessed: false,
		currentLocationID: '',
	},
	mutations: {
		[mutationTypes.setCustomMapData](state, { mapData, exhibitions, locationMap, tours, exhibitionMap }) {
			// note: mapLayerToExhibitionId doesn't normally exist in map-maker
			// index.json output.  It's added to the json file during publish
			// packaging
			const { mapLayerToExhibitionId = [] } = mapData;
			const exhibitionLocationList = exhibitions.reduce(
				(acc, { exhibitionID, triggers }) => [
					...acc,
					...(triggers || []).map(({ locationID }) =>
						Object.assign({ exhibitionID }, locationMap[locationID])
					),
				],
				[]
			);
			const exhibitionPolygonLocations = exhibitionLocationList.reduce((acc, exibitionLocation) => {
				if (exibitionLocation[exibitionLocation.type].shape === 'polygon') {
					Object.assign(acc, { [exibitionLocation.exhibitionID]: exibitionLocation });
				}
				return acc;
			}, {});
			const exhibitLocations = Object.entries(
				exhibitions.reduce(
					(exhibitionsAcc, { exhibitionID, articles }) =>
						Object.assign(exhibitionsAcc, {
							[exhibitionID]: articles.reduce((locationsAcc, [articleID, { triggers }]) => {
								(triggers || []).forEach(({ locationID }) => {
									if (locationID in locationsAcc) return locationsAcc;
									else
										locationsAcc[locationID] = Object.assign(
											{ articles: [articleID] },
											locationMap[locationID]
										);
								});
								return locationsAcc;
							}, {}),
						}),
					{}
				)
			).reduce(
				(acc, [exhibitionID, locationsObject]) =>
					Object.assign(acc, { [exhibitionID]: Object.values(locationsObject) }),
				{}
			);

			const tourPaths = tours.reduce((acc, { pathLocationID, startLocationID, hideTourPathInExhibition }) => {
				if (pathLocationID && !hideTourPathInExhibition) acc.push(pathLocationID);
				if (startLocationID) acc.push(startLocationID);
				return acc;
			}, []);

			const tourLocations = tours.reduce(
				(acc, tour) =>
					Object.assign(acc, {
						[tour._id]: [
							tour.pathLocationID,
							...tour.exhibitIDs.reduce(
								(acc, { articleID }) => [
									...acc,
									...exhibitionMap[tour.exhibitionID].articleMap[articleID].triggers.map(
										({ locationID }) => locationID
									),
								],
								[]
							),
						],
					}),
				{}
			);

			const tourBounds = tours.reduce((acc, tour) => {
				let points = [];
				(tourLocations[tour._id] || []).forEach(locationID => {
					const location = locationMap[locationID];
					if (!location) return;
					points.push(location[location.type].coordinates[0]);
				});
				if (tour.pathLocationID) {
					const location = locationMap[tour.pathLocationID];
					points = points.concat(location[location.type].coordinates);
				}
				if (points.length) {
					acc[tour._id] = geolib.getBounds(points);
				}
				return acc;
			}, {});

			Object.assign(state, {
				mapData,
				tourLocations,
				tourPaths,
				tourBounds,
				exhibitionLocationList,
				exhibitionPolygonLocations,
				exhibitLocations,
				mapLayerToExhibitionId,
			});
		},
		[mutationTypes.setCustomMapLocationID](state, locationID) {
			state.currentLocationID = locationID;
		},
	},
	actions: {
		async [actionTypes.initNavigationMap]({ rootState, getters, dispatch }) {
			// Maps are loaded with the following priority:
			// 1) Exhibition online map
			if (getters.currentExhibition.hasOnlineNavigationMap) {
				//
				dispatch(actionTypes.initOnlineGeoMap, { isExhibitionMap: true });
			}
			// 2) Exhibition custom map
			else if (getters.currentExhibition.customMap) {
				await dispatchCustomMapArticle({
					customMap: getters.currentExhibition.customMap,
					dataSourceCFG: rootState.dataSourceCFG,
					dispatch,
					dataSourceURL: rootState.dataSourceURL, // FIXME legacy fallback
					isExhibitionMap: true,
				});
			}
			// 3) DataSource online map
			else if (rootState.dataSourceCFG.global.hasOnlineNavigationMap) {
				dispatch(actionTypes.initOnlineGeoMap, {});
			}
			// 4) DataSource custom map
			else if (rootState.dataSourceCFG.global.customMap) {
				await dispatchCustomMapArticle({
					customMap: rootState.dataSourceCFG.global.customMap,
					dataSourceCFG: rootState.dataSourceCFG,
					dispatch,
					dataSourceURL: rootState.dataSourceURL, // FIXME legacy fallback
					isExhibitionMap: false,
				});
			}
		},
		async [actionTypes.initOnlineGeoMap](
			{ commit, dispatch, rootState, getters },
			{ distortableImageCorners, languageContent, isExhibitionMap }
		) {
			const geolib = await import(/* webpackChunkName: "geolib" */ 'geolib');
			// set some default bounds so the map does not crash
			let geoBounds = [
				{ lat: 52.3253, lng: 13.245048522949219 },
				{ lat: 52.19919031904484, lng: 13.083343505859375 },
			];
			if (distortableImageCorners) {
				geoBounds = distortableImageCorners;
			} else if (isExhibitionMap && getters.currentExhibition.onlineMapBoundsLocationID) {
				geoBounds =
					rootState.dataSourceCFG.locationMap[getters.currentExhibition.onlineMapBoundsLocationID]
						.geoLocation.coordinates[0];
			} else if (rootState.dataSourceCFG.global.onlineMapBoundsLocationID) {
				geoBounds =
					rootState.dataSourceCFG.locationMap[rootState.dataSourceCFG.global.onlineMapBoundsLocationID]
						.geoLocation.coordinates[0];
			}
			const prelimirayBounds = geoBounds.map(({ lat, lng }) => ({ latitude: lat, longitude: lng }));
			const center = geolib.getCenter(prelimirayBounds);
			const bounds = geolib.getBounds(prelimirayBounds);
			const hasDistortableImage = !!distortableImageCorners;
			const mapData = {
				crs: 'EPSG3857',
				distortableImage: hasDistortableImage
					? `${rootState.dataSourceURL}apps/media-${languageContent[0].coverImage}/2048`
					: undefined,
				isExhibitionMap,
				distortableImageCorners,
				layers: hasDistortableImage
					? [{ templateUrl: 'https://intentional-fake-url.org/' }] // fake URL so no images are loaded
					: [{ templateUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png' }],
				bounds: [[bounds.minLat, bounds.minLng], [bounds.maxLat, bounds.maxLng]],
				center: [center.latitude, center.longitude],
				maxZoom: hasDistortableImage ? 20 : 17,
				minZoom: hasDistortableImage ? 17 : 12,
				attribution: hasDistortableImage
					? ''
					: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
			};
			const { exhibitions, locationMap, tours, exhibitionMap } = rootState.dataSourceCFG;
			commit(mutationTypes.setCustomMapData, { mapData, exhibitions, locationMap, tours, exhibitionMap });
			if (!rootState.isPreview) dispatch(actionTypes.geoLocationStart);
		},
		async [actionTypes.loadCustomMap](
			{ commit, dispatch, rootState },
			{ mapData, articleID, articleVersion, isExhibitionMap }
		) {
			mapData.layers = mapData.layers.map(layer =>
				Object.assign({}, layer, {
					templateUrl: `${rootState.dataSourceURL}apps/article-${articleID}-${articleVersion}${
						layer.templateUrl
						}`,
				})
			);

			mapData.isExhibitionMap = isExhibitionMap;
			const { exhibitions, locationMap, tours, exhibitionMap } = rootState.dataSourceCFG;
			commit(mutationTypes.setCustomMapData, { mapData, exhibitions, locationMap, tours, exhibitionMap });
			// If the map is an geo map start the location API to track the position of the user.
			if (mapData.crs === 'EPSG3857' && !rootState.isPreview) {
				dispatch(actionTypes.geoLocationStart);
			}
		},
		[actionTypes.setCustomMapLocationID]({ commit, rootState, getters }, locationId) {
			let _locationId = '';
			// If the location is set explicitly because a display was clicked on the map.
			if (locationId) {
				_locationId = locationId;
			}
			// If the current exhibition does not have a location do not set a location.
			else if (getters.currentExhibition.triggers && getters.currentExhibition.triggers.length === 0) {
				_locationId = '';
			}
			// If the current article is the landing article use the location of the exhibition.
			else if (getters.currentExhibition.landingArticleID === rootState.currentArticleID) {
				_locationId = getters.currentExhibition.triggers
					? getters.currentExhibition.triggers.find(
						({ locationID }) => rootState.dataSourceCFG.locationMap[locationID].type !== 'beacon'
					).locationID
					: undefined;
			}
			// If the current article has a location on the map.
			else if (getters.currentArticle.triggers.length > 0) {
				_locationId = getters.currentArticle.triggers.find(
					({ locationID }) => rootState.dataSourceCFG.locationMap[locationID].type !== 'beacon'
				).locationID;
			}
			commit(mutationTypes.setCustomMapLocationID, _locationId);
		},
	},
	plugins: [
		store => {
			store.subscribe(mutation => {
				if (mutation.type === mutationTypes.setExhibitionID) {
					// The exhibition has changed, it might have set a different map.
					store.dispatch(actionTypes.initNavigationMap);
					// If the current exhibition doese not have triggers hide the location search results.
					if (
						store.getters.currentExhibition.triggers &&
						!store.getters.currentExhibition.triggers.length
					) {
						store.commit(mutationTypes.viewStateSetUiState, {
							id: uiComponents.uiSearchLocations,
							uiState: uiStates.hidden,
						});
					}
				}
			});
		},
	],
};
